import React from 'react';
import { Plus, Trash2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useSimpleQuoteContext } from "@/contexts/SimpleQuoteContext";

interface QuoteManagerHeaderProps {
  onExportCurrent: () => void;
  onExportAll: () => Promise<void>;
  onExportByCategory: (categoryId: string) => Promise<void>;
  isExporting: boolean;
  onAddQuote: () => void;
}

export function SimpleQuoteManagerHeader({
  onExportCurrent,
  onExportAll,
  onExportByCategory,
  isExporting,
  onAddQuote
}: QuoteManagerHeaderProps) {
  const {
    selectedQuotes,
    handleBatchDelete,
    categories
  } = useSimpleQuoteContext();

  return (
    <div className="flex flex-wrap justify-between items-center gap-2">
      <h2 className="text-xl font-semibold">Quote Manager</h2>
      
      <div className="flex flex-wrap items-center gap-2">
        {selectedQuotes.length > 0 && (
          <Button variant="destructive" onClick={handleBatchDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Selected ({selectedQuotes.length})
          </Button>
        )}
        
        <Button onClick={onAddQuote}>
          <Plus className="mr-2 h-4 w-4" />
          Add Quote
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={isExporting}>
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? "Exporting..." : "Export"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
            <DropdownMenuItem onClick={onExportCurrent}>
              Export Current View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportAll}>
              Export All Quotes
            </DropdownMenuItem>
            
            {categories && categories.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-sm font-semibold">By Category</div>
                {categories.map(category => (
                  <DropdownMenuItem 
                    key={category.id}
                    onClick={() => onExportByCategory(category.id)}
                  >
                    {category.name}
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
